import { getWorkspace, isDesignLike } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import type { NavItem } from "@/mk/types";

export interface AppSection {
    id: string;
    label: string;
    icon: string;
    model?: string;
    group?: string;
    route: string;
    /** shown only to an owner organisation, as the old section was */
    ownerOnly?: boolean;
    modes?: Workspace[];
    designLabel?: string;
    /** what consulting services call it, when it differs from a design's */
    consultingLabel?: string;
    /** the heading this section sits under, as plain text with no icon */
    category?: string;
    /** the short name the trade knows it by, shown after its name: GIR, RFI */
    abbr?: string;
}

/** Where a run of sections is cut into headings. A heading with nothing
 *  under it is never drawn, so losing access to a whole group is harmless. */
interface AppCategory {
    key: string;
    label: string;
    ids: string[];
}

const SETTINGS_CATEGORIES: AppCategory[] = [
    // who may do what is set first, before the data the team works with
    {
        key: "settings.team",
        label: "nav.group.teamAccess",
        ids: ["team", "parties", "users", "delegations", "workflow"],
    },
    {
        key: "settings.masters",
        label: "nav.group.masters",
        ids: ["units", "work_calendars", "job_positions", "attendance_codes"],
    },
    {
        key: "settings.taxonomy",
        label: "nav.group.taxonomy",
        ids: ["request_type", "request_code", "attachments_type", "project_tags"],
    },
    {
        key: "settings.quality",
        label: "nav.group.quality",
        ids: ["concrete_checklist", "quality_control_items"],
    },
    {
        key: "settings.integrations",
        label: "nav.group.integrations",
        ids: ["ai", "notification_settings",
              "alert_rules", "policies"],
    },
];



export interface AppEntry {
    tag: string;
    /** an application that is one list of one model is hidden when that model is */
    model?: string;
    /** what the breadcrumb calls the application, when the sidebar is shorter */
    title?: string;
    id: string;
    label: string;
    icon: string;
    sequence: number;
    route: string;
    group?: string;
    modes?: Workspace[];
    /** Keep routes and breadcrumbs registered when the entry lives in quick links. */
    sidebar?: boolean;
    sections: AppSection[];
}

export interface NavAccess {
    models: Record<string, { read: boolean; write: boolean; create: boolean; unlink: boolean }>;
    boards: Record<string, boolean>;
    apps: Record<string, boolean>;
    groups: Record<string, boolean>;
}

const ORG_ADMIN = "promaster_base.group_org_admin";

const EXECUTION: Workspace[] = ["execution"];

const CONSULTING: Workspace[] = ["consulting"];

const AI_GROUP = "promaster_workflow.pm_admin_document_ai";
/** the dashboards are an administrative permission of their own */
export const DASHBOARD_GROUP = "promaster_workflow.pm_admin_document_dashboard";

/** The requests in the order a project raises them: approvals, inspection,
 *  quantities and changes, then enquiries and stops; one list, no headings. */
const REQUEST_SECTIONS: AppSection[] = [
    { id: "general_approval", abbr: "GAR", label: "nav.req.generalApproval", icon: "fa-file-text-o",
      model: "general.approval.request", route: "/requests/general-approvals" },
    { id: "drawing_approval", abbr: "SDA", label: "nav.req.drawingApproval", icon: "fa-drafting-compass",
      model: "drawing.approval.request", route: "/requests/drawing-approvals" },
    { id: "supplier_approval", abbr: "SAR", modes: EXECUTION, label: "nav.req.supplierApproval", icon: "fa-truck",
      model: "request.supplier.approval", route: "/requests/supplier-approvals" },
    { id: "staff_approval", abbr: "HRA", modes: ["execution", "consulting"], label: "nav.req.staffApproval",
      icon: "fa-id-badge", model: "request.staff.approval", route: "/requests/staff-approvals" },
    { id: "attendance", abbr: "ATT", modes: CONSULTING, label: "nav.req.attendance",
      icon: "fa-calendar-check-o", model: "consulting.attendance.request", route: "/requests/attendance-requests" },
    { id: "material_approval", abbr: "MAR", modes: EXECUTION, label: "nav.req.materialApproval", icon: "fa-cubes",
      model: "request.material.approval", route: "/requests/material-approvals" },
    { id: "sample_approval", abbr: "SMP", modes: EXECUTION, label: "nav.req.sampleApproval", icon: "fa-clone",
      model: "request.sample.approval", route: "/requests/sample-approvals" },
    { id: "inspection_concrete", abbr: "POUR", modes: EXECUTION, label: "nav.req.concreteInspection", icon: "fa-cube",
      model: "inspection.concrete.request", route: "/requests/concrete-inspections" },
    { id: "general_inspection", abbr: "GIR", label: "nav.req.generalInspection", icon: "fa-search",
      designLabel: "nav.req.designInspection",
      model: "general.inspection.request", route: "/requests/inspections" },
    { id: "work_quantity_sheet", abbr: "QTYW", modes: EXECUTION, label: "nav.req.workQuantitySheet", icon: "fa-table",
      model: "work.quantity.sheet", route: "/requests/work-quantity-sheets" },
    { id: "start_new_item", abbr: "SNI", modes: EXECUTION, label: "nav.req.startNewItem", icon: "fa-plus-square",
      model: "start.new.item", route: "/requests/start-new-items" },
    { id: "change_proposal", abbr: "CPR", modes: EXECUTION, label: "nav.req.changeProposal", icon: "fa-exchange",
      model: "change.proposal.request", route: "/requests/change-proposals" },
    { id: "change_request", abbr: "CR", label: "nav.req.changeRequest", icon: "fa-edit",
      model: "change.request", route: "/requests/change-requests" },
    { id: "boq_variance", abbr: "VAR", modes: EXECUTION, label: "nav.req.boqVariance", icon: "fa-balance-scale",
      model: "boq.variance.request", route: "/requests/boq-variance-requests" },
    { id: "boq_amendment", abbr: "BQA", modes: EXECUTION, label: "nav.req.boqAmendment", icon: "fa-random",
      model: "boq.amendment.request", route: "/requests/boq-amendment-requests" },
    { id: "information", abbr: "RFI", modes: EXECUTION, label: "nav.req.information", icon: "fa-info-circle",
      model: "request.information", route: "/requests/rfis" },
    { id: "project_stop", abbr: "S/R", modes: EXECUTION, label: "nav.req.projectStop", icon: "fa-pause-circle",
      model: "project.stop", route: "/requests/project-stops" },
];

export const PROJECT_RESOURCE_SECTIONS: AppSection[] = [
    { id: "materials", label: "nav.settings.materials", icon: "fa-cubes", model: "promaster.material" },
    { id: "partners", modes: EXECUTION, label: "nav.settings.partners", icon: "fa-industry",
      model: "promaster.material.partner" },
    { id: "workforce", label: "nav.settings.workforce", icon: "fa-users", model: "promaster.worker" },
    { id: "equipment", modes: EXECUTION, label: "nav.settings.equipment", icon: "fa-wrench", model: "promaster.equipment" },
].map((section) => ({ ...section, route: `/settings?section=${section.id}` }));

export const SETTINGS_SECTIONS: AppSection[] = [
    { id: "project_tags", label: "nav.settings.projectTags", icon: "fa-tags", model: "project.tags" },
    { id: "units", label: "nav.settings.units", icon: "fa-balance-scale", model: "promaster.unit" },
    // the owner approves the calendars every schedule of its projects works by
    { id: "work_calendars", label: "nav.settings.workCalendars", icon: "fa-calendar",
      model: "resource.calendar", ownerOnly: true },
    { id: "request_type", label: "nav.settings.requestType", icon: "fa-tags", model: "request.type" },
    { id: "request_code", label: "nav.settings.requestCode", icon: "fa-hashtag", model: "request.code" },
    { id: "attachments_type", label: "nav.settings.attachmentsType", icon: "fa-paperclip",
      model: "attachments.type" },
    { id: "concrete_checklist", modes: EXECUTION, label: "nav.settings.concreteChecklist", icon: "fa-cube",
      model: "concrete.checklist" },
    { id: "quality_control_items", modes: EXECUTION, label: "nav.settings.qualityControlItems",
      icon: "fa-check-square-o", model: "quality.control.item" },
    { id: "job_positions", label: "nav.settings.jobPositions", icon: "fa-id-badge",
      model: "project.position" },
    { id: "attendance_codes", modes: CONSULTING, label: "nav.settings.attendanceCodes",
      icon: "fa-calendar-check-o", model: "consulting.attendance.code" },
    { id: "notification_settings", label: "nav.settings.notificationSettings", icon: "fa-bell",
      model: "project.notification.settings" },
    { id: "alert_rules", label: "nav.alertRules", icon: "fa-sliders",
      model: "pm.alert.rule", group: ORG_ADMIN },
    { id: "workflow", label: "nav.settings.workflow", icon: "fa-sitemap", model: "permission.template",
      ownerOnly: true,
      group: ORG_ADMIN },
    { id: "policies", label: "nav.settings.policies", icon: "fa-clock-o",
      group: "promaster_sla.group_read_sla" },
    { id: "team", label: "nav.settings.team", icon: "fa-users", group: ORG_ADMIN },
    { id: "parties", label: "nav.settings.parties", icon: "fa-handshake-o", model: "promaster.party",
      ownerOnly: true },
    { id: "users", label: "nav.settings.users", icon: "fa-user-circle-o", group: ORG_ADMIN },
    { id: "delegations", label: "nav.settings.delegations", icon: "fa-user-times", group: ORG_ADMIN },
    { id: "ai", label: "nav.settings.ai", icon: "fa-magic", model: "majdi.ai.config", group: AI_GROUP },
].map((section) => ({ ...section, route: `/settings?section=${section.id}` }));

export const APPS: AppEntry[] = [
    {
        // the layer above the projects: every workspace's projects, grouped by portfolio
        tag: "promaster_projects.portfolios", id: "portfolios", label: "nav.portfolios",
        icon: "fa-folder-open", sequence: 9, route: "/portfolios",
        model: "project.portfolio", sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "projects", label: "nav.executionProjects",
        icon: "fa-building", sequence: 10, route: "/projects?type=execution",
        model: "project.project", modes: EXECUTION, sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "design-projects", label: "nav.designProjects",
        icon: "fa-pencil-square-o", sequence: 10, route: "/projects?type=design",
        model: "project.project", modes: ["design"], sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "consulting-projects", label: "nav.consultingProjects",
        icon: "fa-lightbulb-o", sequence: 10, route: "/projects?type=consulting",
        model: "project.project", modes: ["consulting"], sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "design-dashboard", label: "nav.designDashboard",
        icon: "fa-tachometer", sequence: 11, route: "/design-dashboard", modes: ["design"],
        group: DASHBOARD_GROUP, sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "consulting-dashboard", label: "nav.consultingDashboard",
        icon: "fa-tachometer", sequence: 11, route: "/design-dashboard", modes: ["consulting"],
        group: DASHBOARD_GROUP, sections: [],
    },
    {
        tag: "promaster_email.email_app", id: "email", label: "nav.email",
        icon: "fa-envelope-open-o", sequence: 62, route: "/email?view=inbox",
        sections: [
            { id: "inbox", label: "nav.inbox", icon: "fa-inbox",
              model: "promaster.mail.message", route: "/email?view=inbox" },
            { id: "outbox", label: "nav.outbox", icon: "fa-paper-plane",
              model: "promaster.mail.message", route: "/email?view=outbox" },
        ],
    },
    {
        tag: "promaster_boq.boq_app", id: "boq", label: "nav.boq",
        icon: "fa-table", sequence: 20, route: "/boq?view=contract",
        sections: [
            { id: "contract", label: "nav.contractBoq", designLabel: "nav.designBoq", icon: "fa-table",
              model: "bill.of.quantities", route: "/boq?view=contract" },
            { id: "team", modes: CONSULTING, label: "nav.teamSpec", icon: "fa-sitemap",
              model: "project.team.position", route: "/boq?view=team" },
            { id: "execution", modes: EXECUTION, label: "nav.boqExecution", icon: "fa-check-square-o",
              model: "bill.of.quantities", route: "/boq?view=execution" },
            { id: "versions", modes: EXECUTION, label: "nav.boqVersions", icon: "fa-clone",
              model: "boq.revision", route: "/boq?view=versions" },
        ],
    },
    {
        tag: "promaster_timeline.timeline_app", id: "timeline", label: "nav.timeline",
        icon: "fa-clock-o", sequence: 30, route: "/timeline", sections: [],
    },
    {
        tag: "promaster_ai.ai_app", id: "ai", label: "nav.ai",
        icon: "fa-magic", sequence: 145, route: "/ai", sidebar: false, sections: [],
    },
    {
        tag: "promaster_base.manual_app", id: "manual", label: "nav.manual",
        icon: "fa-book", sequence: 125, route: "/manual", sections: [],
    },
    {
        tag: "promaster_requests.requests_app", id: "requests", label: "nav.requests",
        icon: "fa-file-text-o", sequence: 40, route: "/requests/general-approvals",
        sections: REQUEST_SECTIONS,
    },
    {
        tag: "promaster_payment.payment_app", id: "payment", label: "nav.paymentCertificates",
        icon: "fa-money", sequence: 50, route: "/payments?view=payments",
        sections: [
            { id: "payments", label: "nav.paymentCertificates", designLabel: "nav.designPayments",
              consultingLabel: "nav.consultingPayments", icon: "fa-money",
              model: "letter.payment", route: "/payments?view=payments" },
            { id: "discounts", label: "nav.ipcDeductions", icon: "fa-percent",
              model: "extract.discount", route: "/payments?view=discounts" },
        ],
    },
    {
        tag: "promaster_letters.letters_app", id: "letters", label: "nav.letters",
        icon: "fa-envelope", sequence: 60, route: "/letters?view=letters",
        sections: [
            { id: "letters", label: "nav.letters", icon: "fa-envelope",
              model: "project.letter", route: "/letters?view=letters" },
            { id: "memos", label: "nav.internalMemos", icon: "fa-file-text-o",
              model: "project.internal.memo", route: "/letters?view=memos" },
            { id: "templates", label: "nav.letterTemplates", icon: "fa-files-o",
              model: "promaster.letter.template", route: "/letters?view=templates" },
        ],
    },
    {
        // the meetings and the committees that hold them, one application
        tag: "promaster_meetings.meetings", id: "meetings", label: "nav.meetingsAndCommittees",
        icon: "fa-users", sequence: 69, route: "/meetings?view=meetings",
        sections: [
            { id: "meetings", label: "nav.meetings", icon: "fa-users",
              model: "meeting.meeting", route: "/meetings?view=meetings" },
            { id: "councils", label: "nav.committees", icon: "fa-sitemap",
              model: "meeting.council", route: "/meetings?view=councils" },
        ],
    },
    {
        tag: "promaster_hse.risk_app", id: "risks", modes: EXECUTION, label: "nav.risks",
        icon: "fa-exclamation-triangle", sequence: 80, route: "/hse?view=risks",
        sections: [
            { id: "risks", label: "nav.riskRegister", icon: "fa-exclamation-triangle",
              model: "promaster.risk.register", route: "/hse?view=risks" },
            { id: "risk-categories", label: "nav.riskCategories", icon: "fa-tags",
              model: "promaster.risk.category", route: "/hse?view=risk-categories" },
        ],
    },
    {
        tag: "promaster_hse.hse_app", id: "hse", modes: EXECUTION, label: "nav.hse",
        icon: "fa-shield", sequence: 81, route: "/hse?view=incidents",
        sections: [
            { id: "incidents", label: "nav.incidents", icon: "fa-ambulance",
              model: "promaster.hse.incident", route: "/hse?view=incidents" },
            { id: "investigation", label: "nav.investigationReports", icon: "fa-gavel",
              model: "investigation.report", route: "/hse?view=investigation" },
            { id: "observations", label: "nav.safetyObservations", icon: "fa-eye",
              model: "promaster.hse.observation", route: "/hse?view=observations" },
            { id: "inspections", label: "nav.safetyInspections", icon: "fa-check-square-o",
              model: "promaster.hse.inspection", route: "/hse?view=inspections" },
            { id: "permits", label: "nav.workPermits", icon: "fa-id-card-o",
              model: "promaster.hse.permit", route: "/hse?view=permits" },
            { id: "toolboxes", label: "nav.toolboxMeetings", icon: "fa-users",
              model: "promaster.hse.toolbox", route: "/hse?view=toolboxes" },
            { id: "actions", label: "nav.correctiveActions", icon: "fa-wrench",
              model: "promaster.hse.action", route: "/hse?view=actions" },
            { id: "incident-categories", label: "nav.incidentCategories", icon: "fa-tags",
              model: "promaster.hse.incident.category", route: "/hse?view=incident-categories" },
            { id: "checklist-items", label: "nav.checklistItems", icon: "fa-list-ul",
              model: "promaster.hse.checklist.item", route: "/hse?view=checklist-items" },
        ],
    },
    {
        tag: "promaster_reports.reports_app", id: "reports", label: "nav.reports",
        icon: "fa-calendar-check-o", sequence: 90, route: "/executive",
        sections: [
            { id: "simplified", modes: EXECUTION, label: "nav.simplifiedReport", icon: "fa-line-chart",
              model: "monthly.report", route: "/executive" },
            { id: "builder", label: "nav.reportBuilder", icon: "fa-magic",
              model: "promaster.report.definition", route: "/report-builder" },
            { id: "daily", modes: EXECUTION, label: "nav.dailyReports", icon: "fa-calendar-check-o",
              model: "daily.report", route: "/reports?view=daily" },
            { id: "weekly", label: "nav.weeklyReports", icon: "fa-calendar",
              model: "weekly.report", route: "/reports?view=weekly" },
            { id: "monthly", label: "nav.monthlyReports", icon: "fa-calendar-o",
              model: "monthly.report", route: "/reports?view=monthly" },
            { id: "non-conformance", modes: EXECUTION, label: "nav.nonConformanceReports",
              icon: "fa-exclamation-triangle", model: "non.conformance.report",
              route: "/reports?view=non-conformance" },
            { id: "quality-control", modes: EXECUTION, label: "nav.qualityControlReports", icon: "fa-check-square-o",
              model: "quality.control.report", route: "/reports?view=quality-control" },
        ],
    },
    {
        tag: "promaster_identity_guest.party_contracts", id: "contracts",
        label: "nav.partyContracts", icon: "fa-id-card", sequence: 98, route: "/contracts",
        model: "promaster.party.contract",
        sections: [],
    },
    {
        tag: "promaster_dashboard.dashboard_app", id: "dashboard", modes: EXECUTION, label: "nav.performanceCenter",
        title: "pmud.title",
        icon: "fa-tachometer", sequence: 100, route: "/dashboard?section=overview",
        group: DASHBOARD_GROUP,
        sections: [
            { id: "overview", label: "nav.allProjects", icon: "fa-line-chart",
              route: "/dashboard?section=overview" },
            { id: "project", label: "nav.projectDetails", icon: "fa-folder-open",
              route: "/dashboard?section=project" },
            { id: "parties", label: "nav.projectParties", icon: "fa-users",
              route: "/dashboard?section=parties" },
            { id: "staff", label: "nav.staffTasks", icon: "fa-id-badge", route: "/staff" },
            { id: "requests", label: "nav.requestsSection", icon: "fa-check-square-o",
              route: "/dashboard?section=requests" },
        ],
    },
    {
        tag: "promaster_handover.handover_app", id: "handover", modes: EXECUTION, label: "nav.handover",
        icon: "fa-handshake-o", sequence: 110, route: "/handover?view=handovers",
        sections: [
            { id: "handovers", label: "nav.handovers", icon: "fa-handshake-o",
              model: "project.handover", route: "/handover?view=handovers" },
            { id: "certificates", label: "nav.completionCertificates", icon: "fa-certificate",
              model: "project.completion.certificate", route: "/handover?view=certificates" },
        ],
    },
    {
        tag: "promaster_settings.project_settings", id: "settings", label: "nav.projectSettings",
        icon: "fa-sliders", sequence: 120, route: "/settings?section=project_tags",
        sections: SETTINGS_SECTIONS,
    },
    {
        tag: "promaster_settings.project_settings", id: "project-resources", label: "nav.projectResources",
        icon: "fa-cubes", sequence: 115, route: "/settings?section=materials",
        sections: PROJECT_RESOURCE_SECTIONS,
    },
    {
        tag: "promaster_archive.archive_app", id: "archive", label: "nav.archive",
        icon: "fa-archive", sequence: 150, route: "/archive?view=projects",
        sections: [
            { id: "projects", label: "nav.projectArchives", icon: "fa-archive",
              route: "/archive?view=projects" },
            { id: "external", label: "nav.externalArchive", icon: "fa-folder-open-o",
              route: "/archive?view=external" },
        ],
    },
];

export const HOME_LINK_GROUPS = ["promaster_reports.group_read_monthly_report", DASHBOARD_GROUP];

function unique(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

export function navigationQuery(): string {
    const tags = unique(APPS.map((app) => app.tag));
    const groups = unique([
        ...APPS.map((app) => app.group),
        ...APPS.flatMap((app) => app.sections.map((section) => section.group)),
        ...HOME_LINK_GROUPS,
    ]);
    const models = unique(APPS.flatMap((app) => app.sections.map((section) => section.model)));
    const params = new URLSearchParams({
        tags: tags.join(","), groups: groups.join(","), models: models.join(","),
    });
    return `/navigation?${params.toString()}`;
}

export function canGroup(access: NavAccess | undefined, group?: string): boolean {
    if (!group || !access || access.groups[group] === undefined) {
        return true;
    }
    return access.groups[group] === true;
}

export function sectionAllowed(access: NavAccess | undefined, section: AppSection,
                               isOwnerOrg?: boolean): boolean {
    return canSection(access, section) && inMode(section, getWorkspace())
        && (!section.ownerOnly || isOwnerOrg === true);
}

function canSection(access: NavAccess | undefined, section: AppSection): boolean {
    if (!canGroup(access, section.group)) {
        return false;
    }
    if (!section.model || !access) {
        return true;
    }
    return access.models[section.model]?.read !== false;
}

function inMode(entry: { modes?: Workspace[] }, mode: Workspace): boolean {
    return !entry.modes || entry.modes.includes(mode);
}

export function appsFor(mode: Workspace = getWorkspace()): AppEntry[] {
    return APPS.filter((app) => inMode(app, mode));
}

export function sectionsFor(app: AppEntry, mode: Workspace = getWorkspace()): AppSection[] {
    return app.sections.filter((section) => inMode(section, mode));
}

export function sectionLabel(section: AppSection, mode: Workspace = getWorkspace()): string {
    if (mode === "consulting" && section.consultingLabel) {
        return section.consultingLabel;
    }
    return isDesignLike(mode) && section.designLabel ? section.designLabel : section.label;
}

function appRoute(app: AppEntry, mode: Workspace): string {
    const sections = sectionsFor(app, mode);
    if (!app.sections.length || sections.some((section) => section.route === app.route)) {
        return app.route;
    }
    return sections[0]?.route ?? app.route;
}

export function navTree(t: (key: string) => string, access: NavAccess | undefined,
                        activeApp?: string, isOwnerOrg?: boolean,
                        mode: Workspace = getWorkspace()): NavItem[] {
    const items: NavItem[] = [];
    for (const app of appsFor(mode).sort((a, b) => a.sequence - b.sequence)) {
        if (app.sidebar === false) {
            continue;
        }
        if (access && access.apps[app.tag] === false) {
            continue;
        }
        if (!canGroup(access, app.group)) {
            continue;
        }
        if (app.model && access && access.models[app.model]?.read === false) {
            continue;
        }
        const allowed = sectionsFor(app, mode)
            .filter((section) => canSection(access, section))
            .filter((section) => !section.ownerOnly || isOwnerOrg === true);
        if (app.sections.length && !allowed.length) {
            continue;
        }
        const children: NavItem[] = [];
        // Grouped applications read better under plain headings: a heading is
        // dropped the moment it has no page under it, so a reader who may open
        // nothing in a group simply does not see that group.
        const groups = app.id === "settings" ? SETTINGS_CATEGORIES : [];
        const grouped = groups.some((entry) => allowed.some(
            (section) => entry.ids.includes(section.id)));
        if (grouped) {
            for (const group of groups) {
                const members = allowed.filter((section) => group.ids.includes(section.id));
                if (!members.length) {
                    continue;
                }
                children.push({ id: `${app.id}:${group.key}`, label: t(group.label), section: t(group.label) });
                children.push(...members.map((section) => ({
                    id: `${app.id}:${section.id}`,
                    label: t(sectionLabel(section, mode)),
                    suffix: section.abbr,
                    icon: section.icon,
                    selectable: true,
                })));
            }
            // A page the groups do not know keeps its place after them.
            const listed = new Set(groups.flatMap((entry) => entry.ids));
            children.push(...allowed.filter((section) => !listed.has(section.id)).map((section) => ({
                id: `${app.id}:${section.id}`,
                label: t(sectionLabel(section, mode)),
                    suffix: section.abbr,
                icon: section.icon,
                selectable: true,
            })));
        } else {
            children.push(...allowed.map((section) => ({
                id: `${app.id}:${section.id}`,
                label: t(sectionLabel(section, mode)),
                    suffix: section.abbr,
                icon: section.icon,
                selectable: true,
            })));
        }
        items.push({
            id: app.id,
            label: t(app.label),
            icon: app.icon,
            selectable: true,
            defaultOpen: app.id === activeApp,
            children: children.length ? children : undefined,
        });
    }
    return items;
}

/** The application's pages in the order its menu shows them: by heading where
 *  it has headings, the pages no heading lists after them. */
function menuOrder(app: AppEntry, mode: Workspace): AppSection[] {
    const sections = sectionsFor(app, mode);
    if (app.id !== "settings") {
        return sections;
    }
    const listed = SETTINGS_CATEGORIES.flatMap((entry) => entry.ids);
    const rank = (section: AppSection) => {
        const position = listed.indexOf(section.id);
        return position < 0 ? listed.length : position;
    };
    return [...sections].sort((a, b) => rank(a) - rank(b));
}

/** Where a menu entry leads. An application opens on the first page of its
 *  menu the reader may open, so each reader lands where their access starts. */
export function routeFor(id: string, mode: Workspace = getWorkspace(),
                         allowed?: (section: AppSection) => boolean): string | null {
    const [appId, sectionId] = id.split(":");
    const app = APPS.find((entry) => entry.id === appId);
    if (!app) {
        return null;
    }
    if (!sectionId) {
        const first = allowed ? menuOrder(app, mode).find(allowed) : undefined;
        return first?.route ?? appRoute(app, mode);
    }
    return sectionsFor(app, mode).find((section) => section.id === sectionId)?.route
        ?? appRoute(app, mode);
}

/** The section the reader is on, as the sidebar marks it.
 *
 *  A section the reader may not open is not in the sidebar at all, so the
 *  application falls back to the first one it does offer — which is what the
 *  page itself shows.
 */
export function activeFor(pathname: string, search: string,
                          allowed?: (app: AppEntry, section: AppSection) => boolean,
                          mode: Workspace = getWorkspace())
        : string | undefined {
    const params = new URLSearchParams(search);
    const apps = appsFor(mode);
    let best: { id: string; length: number } | null = null;
    for (const app of apps) {
        for (const section of sectionsFor(app, mode)) {
            const [path, query] = section.route.split("?");
            const exact = pathname === path && [...new URLSearchParams(query)]
                .every(([key, value]) => params.get(key) === value);
            const bare = section.route.indexOf("?") === -1 && pathname === section.route;
            if (exact || bare) {
                const id = `${app.id}:${section.id}`;
                const weight = section.route.length + (exact ? 1000 : 0);
                if (!best || weight > best.length) {
                    best = { id, length: weight };
                }
            }
        }
    }
    if (best) {
        const [appId, sectionId] = best.id.split(":");
        const app = apps.find((entry) => entry.id === appId);
        const section = app?.sections.find((entry) => entry.id === sectionId);
        if (app && section && allowed && !allowed(app, section)) {
            const first = sectionsFor(app, mode).find((entry) => allowed(app, entry));
            return first ? `${app.id}:${first.id}` : app.id;
        }
        return best.id;
    }
    for (const app of apps) {
        if (pathname === app.route.split("?")[0]) {
            const first = sectionsFor(app, mode)
                .find((section) => section.route.split("?")[0] === pathname);
            return first ? `${app.id}:${first.id}` : app.id;
        }
    }
    return undefined;
}

const APP_MODELS: Record<string, string> = {
    "project.timeline": "/timeline",
};

export function recordPath(model: string, mode: Workspace = getWorkspace(),
                           anyMode = false): string | null {
    const apps = anyMode ? APPS : appsFor(mode);
    for (const app of apps) {
        const sections = anyMode ? app.sections : sectionsFor(app, mode);
        const section = sections.find((entry) => entry.model === model);
        if (section) {
            return section.route;
        }
        if (app.model === model) {
            return app.route;
        }
    }
    return APP_MODELS[model] ?? null;
}

export function recordRoute(model: string, id: number | false | undefined,
                            mode: Workspace = getWorkspace(),
                            anyMode = false): string | null {
    if (!id) {
        return null;
    }
    const route = recordPath(model, mode)
        ?? (anyMode ? recordPath(model, mode, true) : null);
    if (!route) {
        return null;
    }
    const joiner = route.includes("?") ? "&" : "?";
    return `${route}${joiner}open=${id}`;
}

export function trailFor(t: (key: string) => string, active: string | undefined) {
    if (!active) {
        return [];
    }
    const [appId, sectionId] = active.split(":");
    const app = APPS.find((entry) => entry.id === appId);
    if (!app) {
        return [];
    }
    const crumbs = [{ id: `crumb:${active}`, label: t(app.title ?? app.label), icon: app.icon }];
    const section = app.sections.find((entry) => entry.id === sectionId);
    if (section) {
        crumbs.push({ id: `crumb:${section.id}`, label: t(sectionLabel(section)), icon: section.icon });
    }
    return crumbs;
}
